import React, { useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from "react"
import { useSprings, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'

function clamp(num: number, min: number, max: number) {
    return num <= min ? min : num >= max ? max : num;
}

const useChildWidth = (ref: React.RefObject<HTMLElement>, defaultWidth: number = 0) => {
    const [width, setWidth] = React.useState(defaultWidth);
    useEffect(() => {
        const firstChild = ref.current?.children[0] as HTMLElement;
        if(!firstChild) return;
        setWidth(firstChild.offsetWidth);
    })
    return {
        width,
    }
}

type Props = {
    children: React.ReactNode;
    idx?: number;
    slideWidth?: number;
    gap?: number;
    className?: string;
    slideClassName?: string;
    handleIndex?: (index: number) => void;
}

const LegacySlider = forwardRef<HTMLDivElement | null, Props>(({ children, idx = 0, className, slideClassName, slideWidth: _width = 0, gap: _gap = 0, handleIndex }: Props, externalRef) =>{
    const ref = useRef<HTMLDivElement>(null);
    useImperativeHandle(externalRef, () => ref.current as HTMLDivElement, []);
    const size = useMemo( () => React.Children.count(children), [children]);
    const { width: slideWidth } = useChildWidth(ref, _width)
    const index = useRef(0);

    const [props, api] = useSprings(size, i => ({
        x: 0,
    }));

    useMemo(() => {
        index.current = idx
        if(ref.current) {
            api.start(i => ({
                x: -(index.current) *( slideWidth + _gap)
            }))
        }
    }, [idx])

    const bind = useDrag(({ active, movement: [mx], direction: [xDir], cancel }) => {
        if (active && Math.abs(mx) > slideWidth / 3) {
            index.current = clamp(index.current + (xDir > 0 ? -1 : 1), 0, size - 1);
            handleIndex?.(index.current);
            cancel()
        }
        api.start(i => ({
            x: -(index.current) *( slideWidth + _gap) + (active ? mx : 0)
        }))
    }, {
        axis: 'x'
    })

    return (<div ref={ref}
        className={className}
        style={{ userSelect: 'none' }}
        data-testid='swapSlider'
        >
        {props.map(({ x }, i) => (
            <animated.div className={slideClassName} {...bind()} key={i} style={{ x, touchAction: 'pan-y' }}>
                {React.Children.toArray(children)[i]}
            </animated.div>
        ))}
    </div>);
})

export default LegacySlider;